import { useState } from "react";
import {
  Package,
  Plus,
  MoreVertical,
  Edit,
  Loader2,
  Search,
  Trash2,
  Eye,
  ToggleLeft,
  ToggleRight,
  DollarSign,
  Upload,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAdminPendingPacks, useAdminReviewPack } from "@/hooks/use-queries";
import { CheckCircle, XCircle, Clock } from "lucide-react";

function CommunityModerationQueue() {
  const { data: pending, isLoading } = useAdminPendingPacks();
  const review = useAdminReviewPack();

  const act = async (packId: string, approve: boolean) => {
    let note: string | undefined;
    if (!approve) {
      note = window.prompt("Reason for rejection (shown to the publisher):") || undefined;
    }
    try {
      await review.mutateAsync({ packId, approve, note });
      toast.success(approve ? "Pack approved" : "Pack rejected");
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed");
    }
  };

  if (isLoading || !pending || pending.length === 0) return null;

  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-400" />
          Community evals awaiting review
          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">{pending.length}</Badge>
        </CardTitle>
        <CardDescription>
          Approve to make publicly listable and job-requirable. Reject with a reason to send back.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {pending.map((p: any) => (
          <div
            key={p.id}
            className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-border bg-background/40 p-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold truncate">{p.title}</span>
                <span className="text-xs font-mono text-muted-foreground">{p.packId}</span>
                {!p.isFree && (
                  <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">
                    ${Number(p.priceUsdc)} USDC
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {p._count?.items ?? 0} items · by {p.creator?.displayName || p.creator?.email || "unknown"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                onClick={() => act(p.packId, true)}
                disabled={review.isPending}
              >
                <CheckCircle className="h-3.5 w-3.5" /> Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => act(p.packId, false)}
                disabled={review.isPending}
              >
                <XCircle className="h-3.5 w-3.5" /> Reject
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const isValidJson = (str: string): boolean => {
  if (!str.trim()) return true;
  try { JSON.parse(str); return true; } catch { return false; }
};

const tryFormat = (str: string): string => {
  try { return JSON.stringify(JSON.parse(str), null, 2); } catch { return str; }
};

interface QuizPack {
  id: string;
  packId: string;
  title: string;
  description: string | null;
  modeDefault: string;
  priceUsdc: string;
  isFree: boolean;
  certValidityDays: number | null;
  passThreshold: number;
  isActive: boolean;
  order: number;
  _count: {
    items: number;
    sessions: number;
    quizCertifications: number;
    purchases: number;
  };
  revenue?: {
    totalUsdc: string;
    totalPurchases: number;
  };
}

export default function QuizPackManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<QuizPack | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    packId: "",
    title: "",
    description: "",
    modeDefault: "CLOSED_BOOK",
    priceUsdc: "0",
    isFree: true,
    certValidityDays: "",
    passThreshold: "70",
    order: "0",
  });

  // Fetch packs
  const { data: response, isLoading } = useQuery({
    queryKey: ["admin", "eval-packs"],
    queryFn: async () => {
      const res = await api.get("/admin/eval-packs");
      return res.data;
    },
  });

  const packs = response?.data as QuizPack[] | undefined;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await api.post("/admin/eval-packs", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "eval-packs"] });
      toast.success("Quiz pack created successfully");
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to create pack");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ packId, data }: { packId: string; data: any }) => {
      const res = await api.patch(`/admin/eval-packs/${packId}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "eval-packs"] });
      toast.success("Quiz pack updated successfully");
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update pack");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (packId: string) => {
      const res = await api.delete(`/admin/eval-packs/${packId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "eval-packs"] });
      toast.success("Quiz pack deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete pack");
    },
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await api.post("/admin/eval-packs/import", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "eval-packs"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Import failed");
    },
  });

  const handleImport = async () => {
    try {
      const parsed = JSON.parse(importJson);
      const payload = Array.isArray(parsed) ? parsed : [parsed];
      for (const item of payload) {
        await importMutation.mutateAsync(item);
      }
      toast.success(`${payload.length} pack${payload.length > 1 ? "s" : ""} imported`);
      setImportOpen(false);
      setImportJson("");
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        toast.error("Invalid JSON");
      } else {
        toast.error(err.response?.data?.message ?? "Import failed");
      }
    }
  };

  const handleCreate = () => {
    setEditingPack(null);
    setFormData({
      packId: "",
      title: "",
      description: "",
      modeDefault: "CLOSED_BOOK",
      priceUsdc: "0",
      isFree: true,
      certValidityDays: "",
      passThreshold: "70",
      order: "0",
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (pack: QuizPack) => {
    setEditingPack(pack);
    setFormData({
      packId: pack.packId,
      title: pack.title,
      description: pack.description || "",
      modeDefault: pack.modeDefault,
      priceUsdc: pack.priceUsdc,
      isFree: pack.isFree,
      certValidityDays: pack.certValidityDays?.toString() || "",
      passThreshold: pack.passThreshold.toString(),
      order: pack.order.toString(),
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.packId || !formData.title) {
      toast.error("Pack ID and Title are required");
      return;
    }

    const payload = {
      packId: formData.packId,
      title: formData.title,
      description: formData.description || null,
      modeDefault: formData.modeDefault,
      priceUsdc: parseFloat(formData.priceUsdc),
      isFree: formData.isFree,
      certValidityDays: formData.certValidityDays
        ? parseInt(formData.certValidityDays)
        : null,
      passThreshold: parseInt(formData.passThreshold),
      order: parseInt(formData.order),
    };

    if (editingPack) {
      updateMutation.mutate({ packId: editingPack.packId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (packId: string) => {
    if (confirm("Are you sure you want to delete this quiz pack? This cannot be undone.")) {
      deleteMutation.mutate(packId);
    }
  };

  const handleViewItems = (packId: string) => {
    navigate(`/admin/quiz-items/${packId}`);
  };

  const filteredPacks = packs?.filter((pack) =>
    pack.title.toLowerCase().includes(search.toLowerCase()) ||
    pack.packId.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Quiz Pack Management</h1>
          <p className="text-muted-foreground">Create and manage quiz packs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Import JSON
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Create Pack
          </Button>
        </div>
      </div>

      {/* Community moderation queue */}
      <CommunityModerationQueue />

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search packs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Pack List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPacks?.map((pack) => (
            <Card key={pack.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      <CardTitle>{pack.title}</CardTitle>
                      {!pack.isActive && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                      {!pack.isFree && (
                        <Badge variant="default" className="bg-emerald-500">
                          ${pack.priceUsdc} USDC
                        </Badge>
                      )}
                      {pack.isFree && (
                        <Badge variant="outline">Free</Badge>
                      )}
                    </div>
                    <CardDescription>
                      {pack.packId} • {pack._count.items} items • Pass: {pack.passThreshold}%
                    </CardDescription>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(pack)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleViewItems(pack.packId)}>
                        <Eye className="mr-2 h-4 w-4" />
                        View Items
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(pack.packId)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>

              <CardContent>
                {pack.description && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {pack.description}
                  </p>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Sessions</p>
                    <p className="font-semibold">{pack._count.sessions}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Certifications</p>
                    <p className="font-semibold">{pack._count.quizCertifications}</p>
                  </div>
                  {!pack.isFree && pack.revenue && (
                    <>
                      <div>
                        <p className="text-muted-foreground">Purchases</p>
                        <p className="font-semibold">{pack.revenue.totalPurchases}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Revenue</p>
                        <p className="font-semibold">${pack.revenue.totalUsdc} USDC</p>
                      </div>
                    </>
                  )}
                  {pack.isFree && (
                    <div>
                      <p className="text-muted-foreground">Mode</p>
                      <p className="font-semibold">{pack.modeDefault}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {filteredPacks?.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No quiz packs found</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPack ? "Edit Quiz Pack" : "Create Quiz Pack"}
            </DialogTitle>
            <DialogDescription>
              {editingPack
                ? "Update quiz pack configuration"
                : "Create a new quiz pack"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Pack ID */}
            <div>
              <Label htmlFor="packId">Pack ID *</Label>
              <Input
                id="packId"
                placeholder="pack_01_general"
                value={formData.packId}
                onChange={(e) =>
                  setFormData({ ...formData, packId: e.target.value })
                }
                disabled={!!editingPack}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Unique identifier (cannot be changed after creation)
              </p>
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="General Fundamentals"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Core competencies for AI agents..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
              />
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="isFree">Free Pack</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <Switch
                    id="isFree"
                    checked={formData.isFree}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, isFree: checked })
                    }
                  />
                  <Label htmlFor="isFree" className="text-sm font-normal">
                    {formData.isFree ? "Free" : "Paid"}
                  </Label>
                </div>
              </div>

              {!formData.isFree && (
                <div>
                  <Label htmlFor="priceUsdc">Price (USDC)</Label>
                  <Input
                    id="priceUsdc"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="49.00"
                    value={formData.priceUsdc}
                    onChange={(e) =>
                      setFormData({ ...formData, priceUsdc: e.target.value })
                    }
                  />
                </div>
              )}
            </div>

            {/* Pass Threshold & Validity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="passThreshold">Pass Threshold (%)</Label>
                <Input
                  id="passThreshold"
                  type="number"
                  min="60"
                  max="100"
                  placeholder="70"
                  value={formData.passThreshold}
                  onChange={(e) =>
                    setFormData({ ...formData, passThreshold: e.target.value })
                  }
                />
              </div>

              <div>
                <Label htmlFor="certValidityDays">
                  Cert Validity (days)
                </Label>
                <Input
                  id="certValidityDays"
                  type="number"
                  min="1"
                  placeholder="365 (leave empty for no expiry)"
                  value={formData.certValidityDays}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      certValidityDays: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            {/* Mode & Order */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="modeDefault">Default Mode</Label>
                <Select
                  value={formData.modeDefault}
                  onValueChange={(value) =>
                    setFormData({ ...formData, modeDefault: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLOSED_BOOK">Closed Book</SelectItem>
                    <SelectItem value="TOOL_ALLOWED">Tool Allowed</SelectItem>
                    <SelectItem value="WEB_ALLOWED">Web Allowed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({ ...formData, order: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingPack ? "Save Changes" : "Create Pack"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import JSON Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Import Quiz Pack JSON
              <button type="button" onClick={() => setImportJson(j => tryFormat(j))} disabled={!isValidJson(importJson) || !importJson.trim()} className="flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed mr-4">
                <Wand2 className="h-3 w-3" />Format
              </button>
            </DialogTitle>
            <DialogDescription>
              Paste a single pack object or an array of packs. Upserts by packId — safe to re-import.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-muted/20 border border-divider/60 text-xs font-mono text-muted-foreground leading-relaxed whitespace-pre-wrap">
{`{ "packId": "pack_02_coding", "title": "Coding Fundamentals",
  "isFree": true, "passThreshold": 70, "modeDefault": "CLOSED_BOOK",
  "items": [
    { "itemId": "item_02_01", "type": "MCQ", "section": "Basics",
      "prompt": "What is Big-O O(n)?", "timeBudgetSec": 30, "points": 1,
      "options": { "choices": [{"id":"a","text":"Linear"},{"id":"b","text":"Constant"}] },
      "correct": { "optionId": "a" } }
  ] }`}
            </div>
            <Textarea
              className={`font-mono text-xs min-h-[280px] ${importJson.trim() && !isValidJson(importJson) ? "border-destructive focus-visible:ring-destructive" : ""}`}
              placeholder="Paste quiz pack JSON here..."
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
            />
            {importJson.trim() && !isValidJson(importJson) && (
              <p className="text-xs text-destructive">Invalid JSON</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportJson(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || !importJson.trim() || !isValidJson(importJson)}
            >
              {importMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing...</>
              ) : (
                <><Upload className="mr-2 h-4 w-4" />Import</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
